import { OBJECT_STATUS, user_roles } from 'BrdData/Enum';
import UserData from 'BrdData/UserData';
import { CancelButton, StatusToggleButton } from 'Components/Buttons';
import {
  useAlertContext,
  useAuthContext,
  useDrawerContext,
  useToast,
} from 'Context';
import { DataManager } from 'Lib/data';
import { useStateWithRef } from 'Lib/hooks';
import UserInformationPanel from 'Pages/Admin/Users/UserInformationPanel';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { isUserAuthorized } from 'Util';
import UserRolesPanel from './UserRolesPanel';

let DM = new DataManager();

const UserDetails = ({
  userId,
  userObject,
  params,
  isMyAccountPage,
  editable = false,
  ...props
}) => {
  const { showSuccessToast, showToast } = useToast();
  const { appUserId } = useAuthContext();
  const { closeDrawer } = useDrawerContext();

  const [user, setUser, userRef] = useStateWithRef({});
  const idRef = useRef();

  const { openConfirmationDialog } = useAlertContext();

  useEffect(() => {
    DM.add(
      UserData.registerListener('BLANK_RECORD_LOADED', onUserLoaded),
      UserData,
    );
    DM.add(UserData.registerListener('RECORD_LOADED', onUserLoaded), UserData);
    DM.add(
      UserData.registerListener('RECORD_ACTIVATED', onUserActivated),
      UserData,
    );
    DM.add(
      UserData.registerListener('RECORD_DEACTIVATED', onUserDeactivated),
      UserData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    if (!userId && !params?.id && !isMyAccountPage) {
      UserData.getBlank();
    }
  }, []);

  useEffect(() => {
    let currentUserId = (isMyAccountPage && appUserId) || 0;
    if (currentUserId > 0) {
      getUser(currentUserId);
    }
  }, [isMyAccountPage]);

  useEffect(() => {
    if (userId != null) {
      if (!idRef?.current || idRef.current !== userId) {
        getUser(userId);
      }
      idRef.current = userId;
    }
  }, [userId]);

  useEffect(() => {
    if (params?.id) {
      if (!idRef?.current || idRef.current !== params.id) {
        getUser(params.id);
      }
      idRef.current = params.id;
    }
  }, [params]);

  useEffect(() => {
    if (userObject) {
      setUser(userObject);
    }
  }, [userObject]);

  const getUser = (id) => {
    UserData.getRecord(id, {
      _include: 'user_roles',
    });
  };

  const onUserLoaded = (payload) => {
    if (payload) {
      setUser(payload);
    }
  };

  let statusCancelButton =
    !isMyAccountPage &&
    editable &&
    (user?.id ? (
      <StatusToggleButton
        currentStatus={user.status}
        onStatusToggleClicked={() => {
          statusToggleClicked();
        }}
      />
    ) : (
      <CancelButton onCancel={closeDrawer} />
    ));

  const toggleUserStatus = () => {
    userRef.current?.status === OBJECT_STATUS.ACTIVE.key
      ? UserData.deactivateRecord(userRef.current?.id)
      : UserData.activateRecord(userRef.current?.id);
  };

  const statusToggleClicked = () => {
    let currentStatusActive =
      userRef.current?.status === OBJECT_STATUS.ACTIVE.key;
    openConfirmationDialog({
      onConfirm: toggleUserStatus,
      dialogTitle: currentStatusActive
        ? 'Deactivate User?'
        : 'Reactivate User?',
      icon: currentStatusActive ? 'disable' : 'active',
      intent: currentStatusActive ? 'inactive' : 'active',
    });
  };

  const onUserActivated = (payload) => {
    showSuccessToast('User Reactivated');
    getUser(payload.id);
  };

  const onUserDeactivated = (payload) => {
    showSuccessToast('User Deactivated');
    getUser(payload.id);
  };

  return (
    user && (
      <div
        style={{
          display: 'flex',
          gap: 10,
          flexDirection: 'column',
          maxWidth: 600,
        }}>
        <UserInformationPanel title='Details' editable={editable} {...props} />

        {!isMyAccountPage &&
          isUserAuthorized({
            roles: [user_roles.ADMINISTRATOR],
          }) && (
            <UserRolesPanel
              userId={user.id}
              editable={editable && (!!user?.id || user.id === 0)}
              disabled={!user?.id && user.id !== 0}
            />
          )}

        {statusCancelButton}
      </div>
    )
  );
};

UserDetails.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userObject: PropTypes.object,
  params: PropTypes.shape({ id: PropTypes.string }),
  isMyAccountPage: PropTypes.bool,
  editable: PropTypes.bool,
};

export default UserDetails;
